import { useEffect, useMemo, useState } from "react";
import "./App.css";
import { motion } from "framer-motion";

const sectionVariants = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

const highlights = [
  { label: "Professional SWE Experience", value: "1+ Year" },
  { label: "SEO Growth Delivered", value: "53% Avg Increase" },
  { label: "Impressions Impact", value: "1.2M+" },
];

const focusAreas = [
  "Business process automation",
  "Backend services and APIs",
  "Cloud deployment and operations",
  "Reliable implementation and delivery",
];

const experiences = [
  {
    role: "Software Engineer (R&D)",
    company: "Carbarn AU",
    period: "May 2025 – Present",
    points: [
      "Built an automated media pipeline using FastAPI, OpenCV, Pillow, and FFmpeg, deployed on AWS Lambda with Docker and ECR.",
      "Developed compliance form automation that parses automotive inspection PDFs and outputs annotated PDFs plus structured JSON for audit workflows.",
      "Designed and shipped a WhatsApp customer-assistance workflow using Amazon Connect, Lex V2, Bedrock Agents, OpenSearch Serverless, and S3 metadata filtering.",
      "Supported migration from EC2 to ECS (Fargate), added Java Spring Boot pagination, and delivered Next.js frontend integrations.",
      "Implemented Next.js technical SEO best practices (metadata, sitemap, and indexing improvements) and built Python SEO audit tooling, driving 1.2M+ impressions across 9 sites with a 53% average uplift in month one.",
    ],
  },
  {
    role: "AI Trainer",
    company: "Outlier AI",
    period: "Nov 2024 – Mar 2025",
    points: [
      "Improved coding response quality through structured evaluation and refinement workflows across Python, Java, and JavaScript tasks.",
    ],
  },
  {
    role: "Web Developer Intern",
    company: "Workload",
    period: "Jun 2022 – Aug 2022",
    points: [
      "Built interactive frontend experiences in React and JavaScript, improving engagement by 30% and reducing page-load time by 20%.",
    ],
  },
  {
    role: "IT Specialist",
    company: "Rendezvous Academy",
    period: "Apr 2021 – Dec 2021",
    points: [
      "Developed interactive e-learning modules and integrated subscription payments to improve platform functionality and student experience.",
    ],
  },
];

const projects = [
  {
    title: "AWS Employee Room Booking System",
    context: "UBC CPSC 319 • Jan 2024 – Apr 2024",
    description:
      "SaaS booking platform for AWS employees with role-based access and cloud-first architecture, delivered by an 8-member team.",
    link: null,
    badge: "Featured",
    tech: ["React", "Node", "Express", "AWS Lambda", "Cognito", "API Gateway", "MySQL"],
  },
  {
    title: "CanvaSense",
    context: "UBC Learning Analytics Hackathon • Nov 2023",
    description:
      "Runner-up (Wildest Idea): built a Canvas course copilot integrating external APIs and course data to improve student navigation and support.",
    link: "https://github.com/dhawal-rathore/hack-la-2023-lackers",
    badge: "Featured",
    tech: ["React", "Node.js", "Python", "API Integrations", "Canvas API"],
  },
  {
    title: "ProQuest",
    context: "UBC CPSC 455 • May 2023 – Aug 2023",
    description:
      "Led a 5-person team to build a services marketplace and implemented over six REST endpoints connected to a React frontend.",
    link: "https://github.com/sajeedazam/ProQuest",
    badge: "Featured",
    tech: ["React", "Redux", "Node", "Express", "MongoDB", "REST APIs"],
  },
  {
    title: "Evently",
    context: "HackCamp 2023 Hackathon",
    description:
      "Event accessibility visualizer built during HackCamp to help highlight accessibility needs in event planning.",
    link: "https://devpost.com/software/evently-sdj481",
    badge: "Hackathon",
    tech: ["React", "Accessibility", "Hackathon"],
  },
  {
    title: "FocusFHIR",
    context: "nwHacks 2024 Hackathon",
    description:
      "Healthcare-focused hackathon project exploring practical tools for professionals using FHIR-based workflows.",
    link: "https://devpost.com/software/focusfhir",
    badge: "Hackathon",
    tech: ["Healthcare", "Hackathon", "Rapid Prototyping"],
  },
  {
    title: "VehicleManager",
    context: "Academic Project",
    description:
      "Java GUI application for organizing and presenting structured vehicle data through a desktop interface.",
    link: "https://github.com/sajeedazam/VehicleManager",
    badge: "Coursework",
    tech: ["Java", "GUI", "OOP"],
  },
];

const skillGroups = [
  {
    title: "Languages",
    values: ["Python", "Java", "JavaScript"],
  },
  {
    title: "Cloud & Infrastructure",
    values: ["AWS", "Docker", "SaaS", "IaaS"],
  },
  {
    title: "Backend",
    values: ["FastAPI", "Node.js", "Express", "REST APIs", "HTTP"],
  },
  {
    title: "Frontend",
    values: ["React", "Next.js", "JavaScript", "CSS"],
  },
  {
    title: "Tools & Practices",
    values: ["Git", "SEO", "Process Automation"],
  },
];

const skillHighlights = [
  "Translate business/process pain points into practical implementation plans.",
  "Build automation-first workflows with clear operational outcomes.",
  "Ship maintainable systems across backend, cloud, and frontend layers.",
];

const skillPillars = [
  {
    title: "Problem Framing",
    detail:
      "Break ambiguous business issues into clear technical scopes, milestones, and implementation paths.",
  },
  {
    title: "Execution",
    detail:
      "Deliver production-ready features with clean API integration, stable backend logic, and practical UX decisions.",
  },
  {
    title: "Operational Impact",
    detail:
      "Optimize reliability, throughput, and process flow so solutions improve day-to-day team and business outcomes.",
  },
];

const courseworkTracks = [
  {
    title: "Foundations",
    topics: ["Algorithms", "Data Structures", "Software Construction"],
  },
  {
    title: "Systems & Data",
    topics: ["Relational Databases", "Cloud Computing", "Software Engineering Project"],
  },
  {
    title: "Intelligent Systems",
    topics: ["Machine Learning", "Computer Vision", "Applied Industry Practices"],
  },
];

const activities = [
  {
    event: "nwHacks",
    date: "Jan 2024",
    note: "Built and shipped a healthcare-focused prototype under hackathon time constraints.",
  },
  {
    event: "HackCamp",
    date: "Nov 2023",
    note: "Collaborated on accessibility-first problem solving for event planning workflows.",
  },
  {
    event: "UBC Learning Analytics Hackathon",
    date: "Nov 2023",
    note: "Runner-up project focused on improving student course navigation experience.",
  },
];

function App() {
  const [githubContributionCount, setGithubContributionCount] = useState(null);
  const [githubContributions, setGithubContributions] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadContributionCount = async () => {
      try {
        const response = await fetch(
          "https://github-contributions-api.jogruber.de/v4/sajeedazam?y=last"
        );
        if (!response.ok) {
          throw new Error("Failed to load contribution data");
        }

        const data = await response.json();
        const total = data?.total?.lastYear;
        const contributions = Array.isArray(data?.contributions) ? data.contributions : [];

        if (isMounted) {
          setGithubContributionCount(Number.isFinite(total) ? total : null);
          setGithubContributions(contributions);
        }
      } catch {
        if (isMounted) {
          setGithubContributionCount(null);
          setGithubContributions([]);
        }
      }
    };

    loadContributionCount();

    return () => {
      isMounted = false;
    };
  }, []);

  const githubContributionSummary =
    githubContributionCount === null
      ? "Public contributions in the last year."
      : `${new Intl.NumberFormat("en-US").format(
          githubContributionCount
        )} public contributions in the last year.`;

  const githubHeatmap = useMemo(() => {
    if (!githubContributions.length) {
      return { weeks: [], monthLabelsByWeek: {} };
    }

    const toDateKey = (dateObj) =>
      `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(dateObj.getDate()).padStart(2, "0")}`;

    const sorted = [...githubContributions].sort((a, b) => a.date.localeCompare(b.date));
    const firstDate = new Date(`${sorted[0].date}T00:00:00`);
    const lastDate = new Date(`${sorted[sorted.length - 1].date}T00:00:00`);

    const startDate = new Date(firstDate);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const endDate = new Date(lastDate);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const firstDateKey = sorted[0].date;
    const lastDateKey = sorted[sorted.length - 1].date;
    const contributionByDate = new Map(sorted.map((entry) => [entry.date, entry]));
    const weeks = [];

    for (
      const weekStart = new Date(startDate);
      weekStart <= endDate;
      weekStart.setDate(weekStart.getDate() + 7)
    ) {
      const days = [];

      for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        const currentDate = new Date(weekStart);
        currentDate.setDate(weekStart.getDate() + dayIndex);
        const dateKey = toDateKey(currentDate);
        const contribution = contributionByDate.get(dateKey);
        const count = contribution?.count ?? 0;
        const level = contribution?.level ?? 0;
        const inRange = dateKey >= firstDateKey && dateKey <= lastDateKey;
        const dayLabel = new Date(`${dateKey}T00:00:00`).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });

        days.push({
          key: dateKey,
          count,
          level,
          inRange,
          tooltip: inRange
            ? `${count} contribution${count === 1 ? "" : "s"} on ${dayLabel}`
            : `No data for ${dayLabel}`,
        });
      }

      weeks.push(days);
    }

    const monthLabelsByWeek = {};
    weeks.forEach((week, weekIndex) => {
      const monthStart = week.find((day) => day.inRange && day.key.endsWith("-01"));
      if (monthStart) {
        monthLabelsByWeek[weekIndex] = new Date(`${monthStart.key}T00:00:00`).toLocaleString(
          "en-US",
          { month: "short" }
        );
      }
    });

    return { weeks, monthLabelsByWeek };
  }, [githubContributions]);

  return (
    <div className="site-shell" id="top">
      <header className="top-nav">
        <a className="brand" href="#top">
          Sa-Adat-Azam Sajeed
        </a>
        <nav>
          <a href="#experience">Experience</a>
          <a href="#projects">Projects</a>
          <a href="#skills">Skills</a>
          <a href="#education">Education</a>
          <a
            href="/resume/Sa-Adat-Azam%20Sajeed%20Resume.pdf"
            target="_blank"
            rel="noreferrer"
          >
            Resume
          </a>
        </nav>
      </header>

      <main className="content">
        <motion.section
          className="hero"
          initial="hidden"
          animate="show"
          variants={sectionVariants}
        >
          <div className="hero-layout">
            <div className="hero-main">
              <motion.div className="hero-label-row" variants={itemVariants}>
                <p className="eyebrow">Software Engineer • Vancouver, BC</p>
                <span className="code-label">profile.js</span>
              </motion.div>
              <motion.h1 variants={itemVariants}>
                Building practical software that solves business problems and improves operations.
              </motion.h1>
              <motion.p className="lead" variants={itemVariants}>
                I design and ship product-focused systems across backend, cloud, and
                frontend surfaces. My recent work focuses on automation, reliable
                implementations, and measurable process improvements.
              </motion.p>
              <motion.div className="cta-row" variants={itemVariants}>
                <a href="mailto:sajeedazam.20@gmail.com">sajeedazam.20@gmail.com</a>
                <a href="tel:+12368861945">+1 236 886 1945</a>
                <a
                  href="https://github.com/sajeedazam"
                  target="_blank"
                  rel="noreferrer"
                >
                  github.com/sajeedazam
                </a>
                <a
                  href="/resume/Sa-Adat-Azam%20Sajeed%20Resume.pdf"
                  target="_blank"
                  rel="noreferrer"
                >
                  Download Resume
                </a>
              </motion.div>
            </div>

            <motion.aside className="hero-side" variants={itemVariants}>
              <p className="side-title">Core Focus</p>
              <ul className="focus-list">
                {focusAreas.map((area) => (
                  <li key={area}>{area}</li>
                ))}
              </ul>

              <ul className="highlight-grid">
                {highlights.map((item) => (
                  <li key={item.label}>
                    <span>{item.value}</span>
                    <p>{item.label}</p>
                  </li>
                ))}
              </ul>
            </motion.aside>
          </div>
        </motion.section>

        <section
          id="experience"
          className="section section-experience"
        >
          <div className="section-heading">
            <span className="section-icon" aria-hidden="true">
              {"{}"}
            </span>
            <h2>Professional Experience</h2>
            <span className="section-label">experience.js</span>
          </div>
          <p className="section-kicker">
            Roles, ownership, and measurable outcomes across each stage of my journey.
          </p>
          <div className="experience-scroll-timeline">
            {experiences.map((item, index) => (
              <motion.article
                key={`${item.role}-${item.company}`}
                className="timeline-item experience-card"
                initial={{ opacity: 0.2, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <div className="timeline-line-wrap" aria-hidden="true">
                  <span className="timeline-dot" />
                  {index < experiences.length - 1 ? (
                    <span className="timeline-line" />
                  ) : null}
                </div>

                <div className="timeline-content">
                  <p className="timeline-period">{item.period}</p>
                  <div className="card-header">
                    <h3>{item.role}</h3>
                    <span>{item.company}</span>
                  </div>
                  <ul>
                    {item.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <section
          id="projects"
          className="section section-projects"
        >
          <div className="section-heading">
            <span className="section-icon" aria-hidden="true">
              {"[]"}
            </span>
            <h2>Projects</h2>
            <span className="section-label">projects.json</span>
          </div>
          <p className="section-kicker">
            Featured and exploratory builds that reflect product thinking and practical delivery.
          </p>
          <div className="project-grid">
            {projects.map((project) => (
              <article key={project.title} className="project-card">
                <div className="project-meta">
                  <span
                    className={`project-badge ${
                      project.badge === "Featured"
                        ? "project-badge-featured"
                        : "project-badge-archive"
                    }`}
                  >
                    {project.badge}
                  </span>
                  {project.link ? (
                    <a
                      className="project-link"
                      href={project.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View Project
                    </a>
                  ) : (
                    <span className="project-link project-link-disabled">Team/Private</span>
                  )}
                </div>
                <h3>{project.title}</h3>
                <p className="context">{project.context}</p>
                <p>{project.description}</p>
                <div className="tag-row">
                  {project.tech.map((tech) => (
                    <span key={tech}>{tech}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          id="skills"
          className="section section-skills"
        >
          <div className="section-heading">
            <span className="section-icon" aria-hidden="true">
              {"</>"}
            </span>
            <h2>Skills</h2>
            <span className="section-label">skills.map()</span>
          </div>
          <p className="section-kicker">
            Tools and technologies I use to turn requirements into reliable solutions.
          </p>
          <div className="skills-layout">
            <aside className="skills-summary">
              <p className="side-title">How I Deliver</p>
              <ul className="skills-points">
                {skillHighlights.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <div className="skills-focus-card">
                <p className="skills-focus-label">Core Stack</p>
                <p className="skills-focus-value">
                  Python, JavaScript, FastAPI, Node.js, React, AWS, Docker, Next.js
                </p>
              </div>
            </aside>

            <div className="skills-pillars-grid">
              {skillPillars.map((pillar) => (
                <article key={pillar.title} className="skills-pillar-card">
                  <p className="skills-pillar-title">{pillar.title}</p>
                  <p>{pillar.detail}</p>
                </article>
              ))}
            </div>

            <div className="github-heatmap-card skills-heatmap-card">
              <div className="github-heatmap-head">
                <p>GitHub Activity</p>
                <a
                  href="https://github.com/sajeedazam"
                  target="_blank"
                  rel="noreferrer"
                >
                  @sajeedazam
                </a>
              </div>
              <div className="github-heatmap-frame">
                {githubHeatmap.weeks.length ? (
                  <div
                    className="github-heatmap-chart"
                    role="img"
                    aria-label="GitHub contributions heatmap for the last year"
                  >
                    <div className="github-heatmap-months" aria-hidden="true">
                      {githubHeatmap.weeks.map((_, weekIndex) => (
                        <span key={`month-slot-${weekIndex}`} className="github-heatmap-month-slot">
                          {githubHeatmap.monthLabelsByWeek[weekIndex] ? (
                            <i>{githubHeatmap.monthLabelsByWeek[weekIndex]}</i>
                          ) : null}
                        </span>
                      ))}
                    </div>

                    <div className="github-heatmap-grid-wrap">
                      <div className="github-heatmap-weekdays" aria-hidden="true">
                        <span />
                        <span>Mon</span>
                        <span />
                        <span>Wed</span>
                        <span />
                        <span>Fri</span>
                        <span />
                      </div>
                      <div className="github-heatmap-grid">
                        {githubHeatmap.weeks.map((week, weekIndex) => (
                          <div className="github-heatmap-week" key={`week-${weekIndex}`}>
                            {week.map((day) => (
                              <span
                                key={day.key}
                                className={`github-heatmap-cell level-${day.level} ${
                                  day.inRange ? "" : "is-padding"
                                }`}
                                title={day.tooltip}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="github-heatmap-placeholder">Loading activity map...</p>
                )}
              </div>
              <p className="github-heatmap-note">{githubContributionSummary}</p>
            </div>
          </div>

          <div className="skills-grid">
            {skillGroups.map((group) => (
              <article key={group.title} className="skill-group">
                <h3>{group.title}</h3>
                <div className="tag-row">
                  {group.values.map((value) => (
                    <span key={value}>{value}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          id="education"
          className="section section-education"
        >
          <div className="section-heading">
            <span className="section-icon" aria-hidden="true">
              {"#"}
            </span>
            <h2>Education & Activities</h2>
            <span className="section-label">education.md</span>
          </div>
          <p className="section-kicker">
            Academic foundation and team-based hackathon experiences that shaped my approach.
          </p>
          <div className="education-layout">
            <article className="education-card education-main-card">
              <h3>The University of British Columbia</h3>
              <p>BSc in Computer Science • Sep 2019 – Apr 2024</p>
              <p>
                Coursework: Algorithms and Data Structures, Relational Databases, Machine Learning, Computer Vision, Cloud Computing, and Software Engineering Project.
              </p>
              <div className="tag-row education-tags">
                <span>Team-based Delivery</span>
                <span>Product-Oriented Thinking</span>
                <span>Engineering Fundamentals</span>
              </div>
            </article>

            <article className="education-card coursework-card">
              <h3>Relevant Coursework</h3>
              <div className="coursework-grid">
                {courseworkTracks.map((track) => (
                  <div key={track.title} className="coursework-track">
                    <p>{track.title}</p>
                    <div className="tag-row">
                      {track.topics.map((topic) => (
                        <span key={topic}>{topic}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </article>

            <article className="education-card activities-card">
              <h3>Hackathon Activities</h3>
              <ul className="activity-list">
                {activities.map((activity) => (
                  <li key={activity.event}>
                    <div className="activity-header">
                      <strong>{activity.event}</strong>
                      <span>{activity.date}</span>
                    </div>
                    <p>{activity.note}</p>
                  </li>
                ))}
              </ul>
            </article>
          </div>
        </section>
      </main>

      <footer className="footer">
        <p>Open to software engineering roles focused on automation, cloud systems, and full-stack development.</p>
        <a href="mailto:sajeedazam.20@gmail.com">Let&apos;s Connect</a>
      </footer>
    </div>
  );
}

export default App;
